import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { useI18n, BRAND } from "@/i18n";
import { trackPurchase } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 12; // ~24s

export default function PaymentSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | error
  const [attempt, setAttempt] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState("error");
      return;
    }
    try {
      const r = await axios.get(`${API}/payments/status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        setState("paid");
        trackPurchase({ transaction_id: r.data.session_id || sessionId, value: 50, currency: "EUR" });
        return;
      }
      if (["expired", "failed"].includes(r.data.payment_status)) {
        setState("failed");
        return;
      }
      setAttempt((a) => a + 1);
    } catch {
      setState("error");
    }
  }, [sessionId]);

  useEffect(() => {
    if (state !== "polling") return;
    if (attempt >= MAX_ATTEMPTS) {
      setState("error");
      return;
    }
    const timer = setTimeout(poll, attempt === 0 ? 500 : POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [attempt, poll, state]);

  return (
    <div className="min-h-screen bg-[#1F1A14] text-[#FAF9F6] flex items-center justify-center px-4 grain" data-testid="payment-success-page">
      <div className="max-w-xl text-center py-24">
        {state === "polling" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-[#C85A3C] animate-spin" strokeWidth={1.2} />
            <p className="mt-8 overline text-[#C85A3C]">Stripe</p>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-4">{t.booking.checkingPayment}</h1>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#C85A3C]" strokeWidth={1.2} data-testid="payment-success-icon" />
            <p className="mt-8 overline text-[#C85A3C]">{BRAND.name}</p>
            <h1 className="font-serif-display text-5xl md:text-6xl mt-4 leading-[1.05]" data-testid="payment-success-title">
              {t.booking.paidTitle}
            </h1>
            <p className="mt-6 text-[#FAF9F6]/70 text-lg leading-relaxed">
              {t.booking.paidSub}
            </p>
            <Link
              to="/"
              data-testid="payment-back-home"
              className="btn-terracotta inline-block mt-12 px-8 py-4 rounded-sm tracking-[0.2em] uppercase text-xs"
            >
              {t.booking.backHome}
            </Link>
          </>
        )}

        {(state === "failed" || state === "error") && (
          <>
            <AlertCircle className="w-14 h-14 mx-auto text-[#C85A3C]" strokeWidth={1.2} />
            <h1 className="font-serif-display text-4xl md:text-5xl mt-6">{t.booking.cancelTitle}</h1>
            <p className="mt-6 text-[#FAF9F6]/70">{t.booking.cancelSub}</p>
            <Link
              to="/#booking"
              className="btn-terracotta inline-block mt-10 px-8 py-4 rounded-sm tracking-[0.2em] uppercase text-xs"
            >
              {t.booking.retry}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
